import { Currency } from '@next/core-lib/i18n';
import { DataSeries } from '@next/core-lib/models';

import { Barn } from './barn-service.model';
import {
  AnimalGroup,
  BalancePerDeliveryLine,
  BeddingType,
  Delivery,
  FarmResource,
  FarmResourcesDivision,
} from './cycle.model';

export interface Costs {
  water?: number;
  heating?: number;
  electricity?: number;
  cadaver?: number;
  certification?: number;
  healthcare?: number;
  litter?: number;
  catching?: number;
  manure?: number;
  decontamination?: number;
  waterAdditives?: number;
  avined?: number;
  other?: number;
}

export interface DayOldChick {
  id: number;
  barn: Barn;
  arrivalDate: string;
  breed: Breed;
  parentStockIdentifier?: string;
  hatchery: Hatchery;
  chickBirthDate: string;
  amountOfChicks: number;
  chicksPrice?: number;
  chicksWeightInGrams?: number;
  chicksParentsAgeInWeeks?: number;
  amounts: boolean;
  invoiceDate: string;
  invoiceNumber: string;
  vatPercentage?: number;
}

export interface Departure {
  id: number;
  barn: Barn;
  type: string;
  slaughterhouse?: Slaughterhouse;
  amountOfChicks: number;
  departureDate: string;
  footpadLesionScore?: number;
  footpadLesionNonePercentage?: number;
  footpadLesionModeratePercentage?: number;
  footpadLesionSeverePercentage?: number;
  heelDermatitis?: number;
  departureWeight?: number;
  slaughterWeight?: number;
  rejectionAtSlaughterhouseInKg?: number;
  rejectionDuringTransportInKg?: number;
  revenue?: number;
  linkedSlaughterReportId?: number;
  invoiceDate: string;
  invoiceNumber: string;
  vatPercentage?: number;
}

export interface GetCycleResponse {
  cycles: Cycle[];
}

export interface GetConceptsResponse {
  concepts: Concept[];
}

export interface Concept {
  id: number;
  name: string;
  breeds: Breed[];
}

export interface Breed {
  id: number;
  name: string;
}
export interface Hatchery {
  id: number;
  externalId: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
}

export interface Slaughterhouse {
  id: number;
  externalId: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
}

export interface GetProductsToConsumeResponse {
  productsByDeliveries: GetProduct[];
}

export interface GetProduct {
  externalProductId: string;
  productTitle: string;
  unitVolumeType: string;
  totalUnitsDelivered: number;
  totalUnitsConsumed: number;
  totalUnitsRemaining: number;
  deliveriesFound: GetDelivery[];
}

export interface GetDelivery {
  id: number;
  externalDeliveryId: string;
  deliveryType: string;
  deliveryDate: string;
  customerId: number;
  shipToId: number;
  deliveryLines: GetDeliveryLine[];
}

export interface GetDeliveryLine {
  id: string;
  externalUniqueIdentifier: string;
  externalProductId: string;
  productTitle: string;
  unitVolumeType: string;
  unitsDelivered: number;
  unitsRemaining: number;
  isActive: boolean;
  currency: string;
  unitNettoPrice?: number;
  externalInvoiceId: string;
  invoiceDate: string;
  vatPercentage?: number;
  showInFinalSetup?: boolean;
}

export interface Cycle {
  id?: number;
  cycleNumber: number;
  surfaceArea: number;
  concept: Concept;
  cycleDate?: string;
  cycleEndDate?: string;
  dayOldChicks: DayOldChick[];
  departures: Departure[];
  balancesPerDeliveryLine: BalancePerDeliveryLine[];
  isFinalBalance: boolean;
  costs: Costs;
  currency: Currency;
  animalGroup?: AnimalGroup;
  cycleDescription: string;
  finalSetup?: FinalSetup;
  beddingType?: BeddingType;
  beddingAmount?: number;
}

export interface UpsertBalances {
  cycleId: number;
  balancesPerDeliveryLine: BalancePerDeliveryLine[];
  isFinalBalance: boolean;
  editFinalizedBalance: boolean;
}

export interface UpsertCycleResponse {
  id: number;
}
export interface CalculateBalanceResponse {
  cycle: Cycle;
}

export interface GetDeliveriesResponse {
  deliveries: Delivery[];
}

export interface WdoDataSeriesResponse {
  series: DataSeries<number, number>;
}

export interface GrowthDataSeriesResponse {
  series: DataSeries<number, number>;
}

export interface SlaughterReportsResponse {
  slaughterReports: SlaughterReport[];
}

export interface SlaughterInvoicesResponse {
  slaughterInvoices: SlaughterInvoice[];
}

export interface LinkedSlaughterReport {
  cycleId: number;
  barnId: number;
  slaughterReportId: number;
}

export interface SlaughterReport {
  id: number;
  customerId: number;
  locationId: number;
  slaughterHouseId: number;
  linkedCycleId?: number;
  linkedBarnId?: number;
  messageId: string;
  header: SlaughterReportMessageHeader;
  technicalResult: SlaughterEventTechnicalResult;
  financialResult: SlaughterEventFinancialResult;
}

export interface SlaughterInvoice {
  id: number;
  customerId: number;
  locationId: number;
  slaughterhouseId: number;
  linkedCycleId?: number;
  messageId: string;
  issuingDateTime: string;
  amount: number;
  amountIncludingVat: number;
  vatPercentage: number;
  invoiceDate: string;
  invoiceId: string;
}

export interface SlaughterReportMessageHeader {
  id: string;
  type: MessageTypeCodeEnum;
  typeVersion: string;
  issueingDateTime: Date;
  status: string;
  reportPeriodEndDate: Date;
  reportPeriodStartDate: Date;
  languageCode: string;
  senderParty: PartyIdentifier;
  recipientParty: PartyIdentifier;
  location: SlaughterReportSlaughterLocation;
  farmer: SlaughterReportFarmerParty;
  paying: SlaughterReportPayingParty;
  invoicing: SlaughterReportInvoicingParty;
  trader: SlaughterReportTraderParty;
}

export enum MessageTypeCodeEnum {
  TechnicalFinancialResults = 'TechnicalFinancialResults',
  DayOldChickensDelivery = 'DayOldChickensDelivery',
  LoadingInjuries = 'LoadingInjuries',
  TechnicalResults = 'TechnicalResults',
  FinancialResults = 'FinancialResults',
  Invoice = 'Invoice',
  FeedDeliveries = 'FeedDeliveries',
  AnimalGroupInformation = 'AnimalGroupInformation',
  AfterPayment = 'AfterPayment',
}

export interface SlaughterReportAnimalGroup {
  batchId: string;
  age: number;
  breedCode: string;
  categoryCode: string;
  groupId: string;
  groupStartDate: Date;
  houseReferenceId: string;
  productionConceptCode: string;
  animalBatchClassificationCode: number;
}

export interface SlaughterEventTechnicalResult {
  id: string;
  dateTime: Date;
  batchId: string;
  weekNumber: number;
  group: SlaughterReportAnimalGroup;
  slaughterLineId: string;
  sexType: string;
  grossTotalWeight: SlaughterReportUnit;
  netTotalWeight: SlaughterReportUnit;
  numberOfAnimalsDelivered: SlaughterReportUnit;
  averageWeigh: SlaughterReportUnit;
  numberDeadOnArrivalSlaughterhouse: SlaughterReportUnit;
  weightDeadOnArrivalSlaughterhouse: SlaughterReportUnit;
  deadOnArrivalPercentageForAccountSlaughterhouse: SlaughterReportUnit;
  deadOnArrivalWeightForAccountSlaugherhouse: SlaughterReportUnit;
  deadOnArrivalPercentageForAccountProducer: SlaughterReportUnit;
  deadOnArrivalWeightForAccountProducer: SlaughterReportUnit;
  estimatedWeight: SlaughterReportUnit;
  deviationActualAndToBeDeliveredWeight: SlaughterReportUnit;
  numberOfAnimalsSlaughtered: SlaughterReportUnit;
  numberRejectedAnimals: SlaughterReportUnit;
  weightRejectedAnimals: SlaughterReportUnit;
  weightRejectedOrgans: SlaughterReportUnit;
  rejectedPercentageForAccountSlaughterhouse: SlaughterReportUnit;
  rejectedWeightForAccountSlaughterhouse: SlaughterReportUnit;
  rejectedWeightForAccountProducer: SlaughterReportUnit;
  rejectedWeightAnimalsAndOrgans: SlaughterReportUnit;
  weighingChargePercentage: SlaughterReportUnit;
  weighingChargeWeight: SlaughterReportUnit;
  feedingChargePercentage: SlaughterReportUnit;
  feedingChargeWeight: SlaughterReportUnit;
  netWeightPayoutBase: SlaughterReportUnit;
  stockingDensityPerM2: SlaughterReportUnit;
  dailyGrowth: SlaughterReportUnit;
  animalCondition: AnimalConditionOnDelivery;
}

export interface SlaughterEventFinancialResult {
  id: string;
  dateTime: Date;
  weekNumber: number;
  group: SlaughterReportAnimalGroup;
  currencyCode: string;
  unitBaseQuantity: number;
  unitForPricePerUnitCode: number;
  vatPercentage: number;
  calculatedPricePerUnitVatExcluded: number;
  calculatedPricePerUnitVatIncluded: number;
  assessmentCost: number;
  chargeOrPremiumCodeListId: string;
  chargeOrPremiums: ChargeOrPremium[];
}

export interface ChargeOrPremium {
  code: number;
  description: string;
  amount: number;
}

export interface AnimalConditionOnDelivery {
  conditionCharacteristicCodeListId: string;
  overallConditionScore: string;
  conditionScoreLines: AnimalConditionScoreLine[];
}

export interface AnimalConditionScoreLine {
  characteristicCode: number;
  characteristicDescription: string;
  characteristicValue: number;
  characteristicUnit: string;
}

export interface SlaughterReportUnit {
  code: string;
  amount: number;
}

export interface SlaughterReportSlaughterLocation {
  location: PartyIdentifier;
  locationSecondary: PartyIdentifier[];
  partyCharacteristic: PartyCharacteristic;
}

export interface SlaughterReportFarmerParty {
  farmer: PartyIdentifier;
  farmerSecondary: PartyIdentifier[];
  partyCharacteristic: PartyCharacteristic;
}

export interface SlaughterReportTraderParty {
  trader: PartyIdentifier;
  traderSecondary: PartyIdentifier[];
  partyCharacteristic: PartyCharacteristic;
}

export interface SlaughterReportPayingParty {
  partyIdentifier: PartyIdentifier;
  partyCharacteristic: PartyCharacteristic;
}

export interface SlaughterReportInvoicingParty {
  partyIdentifier: PartyIdentifier;
  partyCharacteristic: PartyCharacteristic;
}

export interface PartyCharacteristic {
  bicNumber: string;
  cityName: string;
  cityNamePostBox: string;
  countryCode: string;
  countryCodePostBox: string;
  emailAddress: string;
  iban: string;
  name: string;
  personToContact: string;
  phoneNumber: string;
  postalCodeLocation: string;
  postBoxNumber: string;
  streetNameAndNumber: string;
  telefaxNumber: string;
  url: string;
  vatNumber: string;
}

export interface PartyIdentifier {
  typeCode: string;
  value: string;
}

export interface SlaughterReportBarnLinksRequest {
  barnSlaughterReports: SlaughterReportBarnLink[];
}

export interface SlaughterReportBarnLink {
  slaughterReportId: number;
  barnId: string;
}

export interface FeedAdviceUpdateValidResponse {
  isUsed: boolean;
}

export interface ReferenceValueSheetUpdateValidResponse {
  isUsed: boolean;
}

export interface FinalSetup {
  includeChickDeliveries?: boolean;
  includeFeedDeliveries?: boolean;
  includeDepartures?: boolean;
  includeDiscounts?: boolean;
}

export interface GetFarmResourcesResponse {
  farmResources: FarmResource[];
  cycleResources: FarmResourcesDivision[];
}

export interface UpsertFarmResourceResponse {
  id: number;
}
